import React, { useState } from 'react';
import { samplesAPI, Sample } from '../../services/api';
import { Modal, Button } from '../ui';

interface StatusUpdateModalProps {
  show: boolean;
  sample: Sample;
  onClose: () => void;
  onUpdate: () => void;
}

export const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({ show, sample, onClose, onUpdate }) => {
  const [status, setStatus] = useState(sample.status);
  const [actualDeliveryDate, setActualDeliveryDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const updateData: any = { status };

      if (status === 'DELIVERED' && actualDeliveryDate) {
        updateData.actualDeliveryDate = actualDeliveryDate;
      }

      if (status === 'RETURNED') {
        if (returnDate) updateData.returnDate = returnDate;
        if (returnReason) updateData.returnReason = returnReason;
      }

      await samplesAPI.updateStatus(sample.id, updateData);
      onUpdate();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={show} onClose={onClose} title="Update Sample Status" size="md">
      {error && (
        <div className="mb-4 bg-error-50 border border-error-200 rounded-lg p-3">
          <p className="text-sm text-error-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-neutral-700">
            Status
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            className="mt-1 block w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
          >
            <option value="REQUESTED">Requested</option>
            <option value="PENDING">Pending</option>
            <option value="SHIPPED">Shipped</option>
            <option value="DELIVERED">Delivered</option>
            <option value="UNDER_REVIEW">Under Review</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="RETURNED">Returned</option>
            <option value="LOST">Lost</option>
            <option value="EXPIRED">Expired</option>
          </select>
        </div>

        {status === 'DELIVERED' && (
          <div>
            <label htmlFor="actualDeliveryDate" className="block text-sm font-medium text-neutral-700">
              Actual Delivery Date
            </label>
            <input
              type="date"
              id="actualDeliveryDate"
              value={actualDeliveryDate}
              onChange={(e) => setActualDeliveryDate(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            />
          </div>
        )}

        {status === 'RETURNED' && (
          <>
            <div>
              <label htmlFor="returnDate" className="block text-sm font-medium text-neutral-700">
                Return Date
              </label>
              <input
                type="date"
                id="returnDate"
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
              />
            </div>
            <div>
              <label htmlFor="returnReason" className="block text-sm font-medium text-neutral-700">
                Return Reason
              </label>
              <textarea
                id="returnReason"
                rows={3}
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
                placeholder="Reason for return..."
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={saving}>
            Update Status
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface FeedbackModalProps {
  show: boolean;
  sample: Sample;
  onClose: () => void;
  onUpdate: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ show, sample, onClose, onUpdate }) => {
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(5);
  const [decision, setDecision] = useState<'APPROVED' | 'REJECTED' | 'PENDING'>('PENDING');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!feedback.trim()) {
      setError('Please provide feedback');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await samplesAPI.addFeedback(sample.id, {
        clientFeedback: feedback,
        clientRating: rating,
        clientDecision: decision,
      });
      onUpdate();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add feedback');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={show} onClose={onClose} title="Add Client Feedback" size="md">
      {error && (
        <div className="mb-4 bg-error-50 border border-error-200 rounded-lg p-3">
          <p className="text-sm text-error-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="rating" className="block text-sm font-medium text-neutral-700">
            Rating
          </label>
          <div className="mt-2 flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className="focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-full"
              >
                <svg
                  className={`h-8 w-8 ${star <= rating ? 'text-warning-400' : 'text-neutral-300'} hover:text-warning-400 transition-colors`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </button>
            ))}
            <span className="ml-2 text-sm text-neutral-600">({rating}/5)</span>
          </div>
        </div>

        <div>
          <label htmlFor="feedback" className="block text-sm font-medium text-neutral-700">
            Feedback
          </label>
          <textarea
            id="feedback"
            rows={4}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            placeholder="Enter client feedback..."
            required
          />
        </div>

        <div>
          <label htmlFor="decision" className="block text-sm font-medium text-neutral-700">
            Decision
          </label>
          <select
            id="decision"
            value={decision}
            onChange={(e) => setDecision(e.target.value as any)}
            className="mt-1 block w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
          >
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={saving}>
            Add Feedback
          </Button>
        </div>
      </form>
    </Modal>
  );
};
